/**
 * OS display scaling utility.
 *
 * Previously this applied a CSS zoom to #root to shrink the UI at higher DPR
 * values (150%/175%/200%) so it matched the 125% layout. That approach caused
 * the admin page (and other views) to appear over-zoomed/compacted.
 *
 * New strategy: let the browser render at its native DPR. The CSS layouts are
 * now fluid enough to handle the slightly smaller CSS viewport at 150% scaling
 * without needing artificial zoom compensation. Only extreme DPR bands (175%+)
 * still get a mild compensation to prevent breakpoint collapse on large screens.
 *
 * For 150% scaling we add a CSS class `emcure-dpr-150` on <html> so that
 * targeted CSS rules can slightly reduce font sizes and adjust spacing without
 * zooming the entire page.
 *
 * DPR bands (Windows):
 *   1.25 → 125%  — no adjustment (reference)
 *   1.5  → 150%  — NO zoom; CSS class for minor text/spacing tweaks
 *   1.75 → 175%  — mild zoom = 1.25/1.75 ≈ 0.7143
 *   2.0  → 200%  — mild zoom = 1.25/2.00 = 0.6250
 */

function compactZoomForDpr(dpr) {
  if (dpr >= 1.9  && dpr < 2.2)  return 1.25 / 2.0;   // 200% → match 125%
  if (dpr >= 1.65 && dpr < 1.9)  return 1.25 / 1.75;  // 175% → match 125%
  if (dpr >= 1.35 && dpr < 1.65) return 1.25 / 1.5;   // 150% → match 125%
  return 1;
}

/** Returns true when the display is at ~150% OS scaling */
function isDpr150(dpr) {
  return dpr >= 1.35 && dpr < 1.65;
}

export function getOsDisplayCompactZoom() {
  if (typeof window === "undefined") return 1;
  return compactZoomForDpr(window.devicePixelRatio || 1);
}

function clearCompactStyles(root) {
  root.style.removeProperty("zoom");
  root.style.removeProperty("width");
  root.style.removeProperty("height");
  root.style.removeProperty("min-height");
  root.style.removeProperty("position");
  root.style.removeProperty("top");
  root.style.removeProperty("left");
  root.style.removeProperty("right");
  root.style.removeProperty("bottom");
  root.style.removeProperty("margin");
  delete root.dataset.osCompactZoom;
  document.documentElement.style.removeProperty("--os-compact-zoom");
  document.documentElement.classList.remove("emcure-os-compact-zoom");
  document.body.style.removeProperty("max-width");
}

export function applyOsDisplayScaleToRoot() {
  const root = document.getElementById("root");
  if (!root) return;

  const dpr = window.devicePixelRatio || 1;

  // Remove the 150% class — we now zoom-compensate instead
  document.documentElement.classList.remove("emcure-dpr-150");

  const z = getOsDisplayCompactZoom();
  if (z === 1) {
    clearCompactStyles(root);
    return;
  }

  root.dataset.osCompactZoom = String(z);
  document.documentElement.style.setProperty("--os-compact-zoom", String(z));
  document.documentElement.classList.add("emcure-os-compact-zoom");

  // Out of normal flow: pre-zoom width/height are > 100vw/100vh but must not
  // grow the document (which would show a page scrollbar).
  root.style.position = "fixed";
  root.style.top = "0";
  root.style.left = "0";
  root.style.margin = "0";
  root.style.zoom = String(z);
  root.style.width = `calc(100vw / ${z})`;
  root.style.height = `calc(100vh / ${z})`;

  document.body.style.maxWidth = "none";
}
